<template>
  <InfoPopupComponent
    class="label"
    :content-style="`max-width: 300px; ${infoStyle}`"
    :disabled="disabled"
    :is-static="!hasInfoContent"
    :fa-icon="!hasInfoContent || showPropertyDeadlineInfo ? '' : 'fa-info-circle'"
    tabindex="-1"
    :class="{
      deadline: showPropertyDeadlineInfo,
    }"
  >
    <!-- :class="{ deadline: showPropertyDeadlineInfo && !disabled }" -->
    <template #label>
      <span v-if="!disabled && showPropertyDeadlineInfo" class="info-circle mr-1">
        {{ propertyDeadlineInfo?.level }}
      </span>

      <span class="property-label">
        {{ label }}

        <template v-if="!disabled">
          <i
            v-if="property.allowMultipleValues && (property.hasTerms || property.type !== 'attribute')"
            class="fa fa-clone"
            style="font-size: 0.8em; margin-right: 2px"
            :title="appTranslate('Allows multiple values')"
          />
          <i v-if="property.undeletable" class="fa fa-ban" style="font-size: 0.8em; margin-right: 2px" :title="appTranslate('Value not deletable')" />
        </template>
      </span>

      <CultureSpecificAttribute v-if="country || language" :country="country" :language="language" class="cultural" />

      <CultureSpecificAttribute
        v-for="exclusiveCountry of property?.exclusiveToCountries"
        :key="exclusiveCountry"
        :country="exclusiveCountry"
        class="cultural"
      />
    </template>
    <template #content>
      <div v-if="showContentLabel" class="content-label">{{ label }}</div>
      {{ props.property.info?.[store.infoLanguage] }}
      <div v-if="belongsTo" class="font-weight-bold" :class="{ 'mt-2': props.property.info?.[store.infoLanguage] }">
        {{ belongsTo.join('\n') }}
      </div>
    </template>
  </InfoPopupComponent>
</template>

<script setup lang="ts">
import { usePropertyStore } from '@/store/propertyStore'
import { useTranslation } from '@/composables/useTranslation'
import { Country, Culture, CultureSpecificity, Language } from '@/models/Global'
import { AdvancedProperty } from '@/models/Property'
import PropertyService from '@/services/PropertyService'
import { useDefaultStore } from '@/store'
import { computed } from 'vue'
import CultureSpecificAttribute from '../simple/CultureSpecificAttribute.vue'
import InfoPopupComponent from '../simple/InfoPopupComponent.vue'

// -----------
// -- Setup --
// -----------
const props = defineProps<{
  property: AdvancedProperty
  culture?: Culture
  disabled?: boolean // will not show icons or popups
  showContentLabel?: boolean
  infoStyle?: string
  noValue?: boolean
}>()
const store = useDefaultStore()
const propertyStore = usePropertyStore()
const { appTranslate } = useTranslation()
const { getProfilesThatPropertyBelongsTo } = usePropertyStore()

// --------------
// -- Computed --
// --------------
const label = computed(() => PropertyService.getTranslatedHeader(props.property, store.translationLanguage))

const country = computed(() => {
  if (props.property.type === 'countrySpecific') {
    return (props.property.culturePrefix as Country) || props.culture?.country
  }
  return undefined
})

const language = computed(() => {
  if (props.property.type === 'languageSpecific') {
    return (props.property.culturePrefix as Language) || props.culture?.language
  }
  return undefined
})

const hasInfoContent = computed(() => props.property.info?.[store.infoLanguage] || belongsTo.value?.length || props.showContentLabel)

const belongsTo = computed(() => getProfilesThatPropertyBelongsTo(props.property))

const showPropertyDeadlineInfo = computed(() => propertyDeadlineInfo.value && propertyStore.showColouredDeadlineHeaders)

const propertyDeadlineInfo = computed(() => propertyStore.deadlinesByKey?.[props.property.key])
const propertyDeadlineColour = computed(() => propertyDeadlineInfo.value?.colour)
</script>

<style lang="scss" scoped>
.content-label {
  font-weight: bold;
  font-size: 1.1em;
  margin-bottom: 0.3rem;
}

.cultural {
  height: 1.3rem;
}

.label.deadline {
  &,
  // using these selectors only to increase specificity, to make sure deadline colours overrides preliminary
  &.no-value {
    color: v-bind(propertyDeadlineColour) !important;

    .info-circle {
      background-color: v-bind(propertyDeadlineColour);
    }
  }
}
</style>
